<template>
    <div>
        <div
            class="kgr-row"
            style="display: flex; justify-content: space-between; align-items: center"
        >
            <kgr-checkbox
                :label="$t('Planungsmodus')"
                @click="togglePlanningMode()"
            />
            <a
                :href="excelUrl"
                class="kgr-button kgr-button--accent"
            >{{ $t('Excel-Download') }}</a>
        </div>

        <Loading v-if="loading" />
        <table
            v-else
            class="kgr-table kgr-table--block table-groups printable kgr-row"
        >
            <thead>
                <tr class="kgr-table__row">
                    <th class="kgr-table__cell kgr-table__cell--headline"></th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('EmployeeChildRatio.overview.hours') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('EmployeeChildRatio.overview.factor') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('EmployeeChildRatio.overview.numberOfChildren') }}
                        <kgr-icon
                            v-if="planningMode"
                            icon-class="info-circle"
                            style="margin-bottom: -5px;margin-right:5px;color:white;"
                            disabled
                            small
                            :title="$t('Die Anzahl der Kinder kann zur Analyse temporär verändert werden.')"
                        />
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('EmployeeChildRatio.overview.staffHoursDaily') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('EmployeeChildRatio.overview.staffHoursWeekly') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('EmployeeChildRatio.overview.fullTimeStaff') }}
                    </th>
                </tr>
            </thead>
            <tbody>
                <template v-for="hoursCategoryContainer in all">
                    <tr
                        v-for="(hoursCategory, index) in (planningMode ? hoursCategoryContainer.all : hoursCategoryContainer.all.filter(c => c.originalNumberOfChildren > 0))"
                        :key="hoursCategory.id"
                        class="kgr-table__row"
                        :class="{ 'is-striped': index % 2 === 1 }"
                    >
                        <td class="kgr-table__cell">
                            {{ hoursCategory.name }}
                        </td>
                        <td class="kgr-table__cell">
                            {{ hoursCategory.hoursToShow }}
                        </td>
                        <td class="kgr-table__cell kgr-table__cell--numeric">
                            {{ round(hoursCategory.factorToShow) }}
                        </td>
                        <td
                            class="kgr-table__cell"
                            style="display: flex; justify-content: space-between; align-items: center;"
                        >
                            <div>
                                <span
                                    v-show="planningMode"
                                    style="user-select: none;"
                                >
                                    <kgr-icon
                                        icon-class="remove"
                                        tiny
                                        @click="decrementAdjustedValue(hoursCategory)"
                                    />
                                    {{ adjustedValues[`${hoursCategory.name} ${hoursCategory.hoursToShow}`]?.value ?? hoursCategory.numberOfChildren }}
                                    <kgr-icon
                                        icon-class="add"
                                        tiny
                                        @click="incrementAdjustedValue(hoursCategory)"
                                    />
                                </span>
                            </div>
                            <div>
                                <span :style="planningMode && adjustedValues[`${hoursCategory.name} ${hoursCategory.hoursToShow}`] && hoursCategory.originalNumberOfChildren !== adjustedValues[`${hoursCategory.name} ${hoursCategory.hoursToShow}`].value ? 'text-decoration: line-through;' : ''">
                                    {{ hoursCategory.originalNumberOfChildren }}
                                </span>
                            </div>
                        </td>
                        <td class="kgr-table__cell kgr-table__cell--numeric">
                            {{ round(hoursCategory.staffHoursDaily) }}
                        </td>
                        <td class="kgr-table__cell kgr-table__cell--numeric">
                            {{ round(hoursCategory.staffHoursWeekly) }}
                        </td>
                        <td class="kgr-table__cell kgr-table__cell--numeric">
                            {{ round(hoursCategory.fullTimeStaff) }}
                        </td>
                    </tr>
                    <tr
                        v-if="planningMode ? hoursCategoryContainer.all.length > 0 : hoursCategoryContainer.all.some(c => c.originalNumberOfChildren > 0)"
                        :key="`${hoursCategoryContainer.name}-subOverall`"
                        class="kgr-table__row is-striped"
                    >
                        <td class="kgr-table__cell kgr-text kgr-text--bold">
                            {{ hoursCategoryContainer.name }} {{ $t('EmployeeChildRatio.overview.subOverall') }}
                        </td>
                        <td class="kgr-table__cell">
                            &nbsp;
                        </td>
                        <td class="kgr-table__cell">
                            &nbsp;
                        </td>
                        <td class="kgr-table__cell kgr-table__cell--numeric kgr-text kgr-text--bold">
                            {{ hoursCategoryContainer.numberOfChildren }}
                        </td>
                        <td class="kgr-table__cell kgr-table__cell--numeric kgr-text kgr-text--bold">
                            {{ round(hoursCategoryContainer.staffHoursDaily) }}
                        </td>
                        <td class="kgr-table__cell kgr-table__cell--numeric kgr-text kgr-text--bold">
                            {{ round(hoursCategoryContainer.staffHoursWeekly) }}
                        </td>
                        <td class="kgr-table__cell kgr-table__cell--numeric kgr-text kgr-text--bold">
                            {{ round(hoursCategoryContainer.fullTimeStaff) }}
                        </td>
                    </tr>
                </template>
                <tr
                    v-for="addition in additions"
                    :key="addition.name"
                    class="kgr-table__row"
                >
                    <td class="kgr-table__cell">
                        {{ addition.name }}
                        <kgr-icon
                            v-if="addition.showIntegrationTooltip"
                            alt=""
                            icon-class="info-circle"
                            style="margin-bottom: -5px;margin-right:5px;color:black;"
                            disabled
                            small
                            :title="$t('EmployeeChildRatioHint.IntegrationTyp.hint')"
                        />
                    </td>
                    <td class="kgr-table__cell">
                        &nbsp;
                    </td>
                    <td class="kgr-table__cell kgr-table__cell--numeric">
                        {{ addition.factor }}
                    </td>
                    <td class="kgr-table__cell kgr-table__cell--numeric">
                        {{ addition.numberOfChildren }}
                    </td>
                    <td class="kgr-table__cell kgr-table__cell--numeric">
                        {{ round(addition.staffHoursDaily) }}
                    </td>
                    <td class="kgr-table__cell kgr-table__cell--numeric">
                        {{ round(addition.staffHoursWeekly) }}
                    </td>
                    <td class="kgr-table__cell kgr-table__cell--numeric">
                        {{ round(addition.fullTimeStaff) }}
                    </td>
                </tr>
                <tr class="kgr-table__row is-striped">
                    <td class="kgr-table__cell kgr-text kgr-text--bold">
                        {{ $t('EmployeeChildRatio.overview.subtotal') }}
                    </td>
                    <td class="kgr-table__cell">
                        &nbsp;
                    </td>
                    <td class="kgr-table__cell">
                        &nbsp;
                    </td>
                    <td class="kgr-table__cell kgr-table__cell--numeric kgr-text kgr-text--bold">
                        {{ categoryContainer.numberOfChildren }}
                    </td>
                    <td class="kgr-table__cell kgr-table__cell--numeric kgr-text kgr-text--bold">
                        {{ round(categoryContainer.staffHoursDaily) }}
                    </td>
                    <td class="kgr-table__cell kgr-table__cell--numeric kgr-text kgr-text--bold">
                        {{ round(categoryContainer.staffHoursWeekly) }}
                    </td>
                    <td class="kgr-table__cell kgr-table__cell--numeric kgr-text kgr-text--bold">
                        {{ round(categoryContainer.fullTimeStaff) }}
                    </td>
                </tr>
                <tr
                    v-if="hasAdminHours"
                    class="kgr-table__row"
                >
                    <td class="kgr-table__cell">
                        {{ $t('EmployeeChildRatio.overview.adminHours') }}
                    </td>
                    <td class="kgr-table__cell">
                        &nbsp;
                    </td>
                    <td class="kgr-table__cell">
                        &nbsp;
                    </td>
                    <td class="kgr-table__cell">
                        &nbsp;
                    </td>
                    <td class="kgr-table__cell kgr-table__cell--numeric">
                        {{ round(adminHours.daily) }}
                    </td>
                    <td class="kgr-table__cell kgr-table__cell--numeric">
                        {{ round(adminHours.weekly) }}
                    </td>
                    <td class="kgr-table__cell kgr-table__cell--numeric">
                        {{ round(adminHours.fullTimeStaff) }}
                    </td>
                </tr>
                <tr
                    v-if="assistantHours.daily > 0"
                    class="kgr-table__row is-striped"
                >
                    <td class="kgr-table__cell">
                        {{ $t('EmployeeChildRatio.overview.assistantHours') }}
                    </td>
                    <td class="kgr-table__cell">
                        &nbsp;
                    </td>
                    <td class="kgr-table__cell">
                        &nbsp;
                    </td>
                    <td class="kgr-table__cell">
                        &nbsp;
                    </td>
                    <td class="kgr-table__cell kgr-table__cell--numeric">
                        {{ round(assistantHours.daily) }}
                    </td>
                    <td class="kgr-table__cell kgr-table__cell--numeric">
                        {{ round(assistantHours.weekly) }}
                    </td>
                    <td class="kgr-table__cell kgr-table__cell--numeric">
                        {{ round(assistantHours.fullTimeStaff) }}
                    </td>
                </tr>
                <tr
                    v-if="otherHours.daily > 0"
                    class="kgr-table__row is-striped"
                >
                    <td class="kgr-table__cell">
                        {{ $t('EmployeeChildRatio.overview.otherHours') }}
                    </td>
                    <td class="kgr-table__cell">
                        &nbsp;
                    </td>
                    <td class="kgr-table__cell">
                        &nbsp;
                    </td>
                    <td class="kgr-table__cell">
                        &nbsp;
                    </td>
                    <td class="kgr-table__cell kgr-table__cell--numeric">
                        {{ round(otherHours.daily) }}
                    </td>
                    <td class="kgr-table__cell kgr-table__cell--numeric">
                        {{ round(otherHours.weekly) }}
                    </td>
                    <td class="kgr-table__cell kgr-table__cell--numeric">
                        {{ round(otherHours.fullTimeStaff) }}
                    </td>
                </tr>
                <tr
                    v-if="hasOffTimeMultiplier"
                    class="kgr-table__row is-striped"
                >
                    <td class="kgr-table__cell">
                        {{ $t('EmployeeChildRatio.overview.offTimeMultiplier') }}
                    </td>
                    <td class="kgr-table__cell">
                        &nbsp;
                    </td>
                    <td class="kgr-table__cell">
                        &nbsp;
                    </td>
                    <td class="kgr-table__cell">
                        &nbsp;
                    </td>
                    <td class="kgr-table__cell kgr-table__cell--numeric">
                        {{ round(categoryContainer.offTimeHoursDaily) }}
                    </td>
                    <td class="kgr-table__cell kgr-table__cell--numeric">
                        {{ round(categoryContainer.offTimeHoursWeekly) }}
                    </td>
                    <td class="kgr-table__cell kgr-table__cell--numeric">
                        {{ round(categoryContainer.offTimeFullTimeStaff) }}
                    </td>
                </tr>
                <tr class="kgr-table__row is-striped">
                    <td class="kgr-table__cell kgr-text kgr-text--bold">
                        {{ (show95PercentRow ? $t('EmployeeChildRatio.overview.overall100') : $t('EmployeeChildRatio.overview.overall')) }}
                    </td>
                    <td class="kgr-table__cell">
                        &nbsp;
                    </td>
                    <td class="kgr-table__cell">
                        &nbsp;
                    </td>
                    <td class="kgr-table__cell kgr-table__cell--numeric kgr-text kgr-text--bold">
                        {{ categoryContainer.numberOfChildren }}
                    </td>
                    <td class="kgr-table__cell kgr-table__cell--numeric kgr-text kgr-text--bold">
                        {{ round(overall['100percent'].daily) }}
                    </td>
                    <td class="kgr-table__cell kgr-table__cell--numeric kgr-text kgr-text--bold">
                        {{ round(overall['100percent'].weekly) }}
                    </td>
                    <td class="kgr-table__cell kgr-table__cell--numeric kgr-text kgr-text--bold">
                        {{ round(overall['100percent'].fullTimeStaff) }}
                    </td>
                </tr>
                <tr
                    v-if="show95PercentRow"
                    class="kgr-table__row is-striped"
                >
                    <td class="kgr-table__cell kgr-text kgr-text--bold">
                        {{ $t('EmployeeChildRatio.overview.overall95') }}
                    </td>
                    <td class="kgr-table__cell">
                        &nbsp;
                    </td>
                    <td class="kgr-table__cell">
                        &nbsp;
                    </td>
                    <td class="kgr-table__cell kgr-table__cell--numeric kgr-text kgr-text--bold">
                        {{ categoryContainer.numberOfChildren }}
                    </td>
                    <td class="kgr-table__cell kgr-table__cell--numeric kgr-text kgr-text--bold">
                        {{ round(overall['95percent'].daily) }}
                    </td>
                    <td class="kgr-table__cell kgr-table__cell--numeric kgr-text kgr-text--bold">
                        {{ round(overall['95percent'].weekly) }}
                    </td>
                    <td class="kgr-table__cell kgr-table__cell--numeric kgr-text kgr-text--bold">
                        {{ round(overall['95percent'].fullTimeStaff) }}
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>

<script>
import Icon from '../../components/common/icon/Icon.vue';
import KgrCheckbox from '../../components/common/form/checkbox/Checkbox.vue';
import Loading from '../../components/common/Loading.vue';
import axios from 'axios';

export default {
    components: {
        KgrCheckbox,
        Loading,
        'kgr-icon': Icon,
    },
    props: {
        date: {
            type: String,
            required: true,
        },
        groupId: {
            type: Number,
            required: false,
            default: null,
        },
        excelBaseUrl: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            loading: true,
            all: [],
            additions: [],
            assistantHours: {
                daily: 0,
                weekly: 0,
                fullTimeStaff: 0,
            },
            otherHours: {
                daily: 0,
                weekly: 0,
                fullTimeStaff: 0,
            },
            adminHours: {
                daily: 0,
                weekly: 0,
                fullTimeStaff: 0,
            },
            overall: {
                '100percent': {
                    daily: 0,
                    weekly: 0,
                    fullTimeStaff: 0,
                },
                '95percent': {
                    daily: 0,
                    weekly: 0,
                    fullTimeStaff: 0,
                },
            },
            hasAdminHours: false,
            hasOffTimeMultiplier: false,
            show95PercentRow: false,
            planningMode: false,
            adjustedValues: {},
        };
    },
    computed: {
        excelUrl() {
            const adjustedValues = this.planningMode
                ? Object.entries(this.adjustedValues).map(([k, v]) => ({ name: k, ...v }))
                : [];
            return `${this.excelBaseUrl}?adjustedValues=${encodeURIComponent(JSON.stringify(adjustedValues))}`;
        },
    },
    async created() {
        await this.fetchData();
        this.loading = false;

        this.adjustedValues = {};
        for (const hoursCategoryContainer of this.all) {
            for (const hoursCategory of hoursCategoryContainer.all) {
                const key = `${hoursCategory.name} ${hoursCategory.hoursToShow}`;
                this.$set(this.adjustedValues, key, { value: hoursCategory.numberOfChildren, category: hoursCategory.category, weeklyHours: hoursCategory.weeklyHours });
            }
        }
    },
    methods: {
        async fetchData() {
            let url = `/employee-child-ratio/${this.date}`;
            if (this.groupId !== null) {
                url += `/${this.groupId}`;
            }
            const adjustedValues = this.planningMode ? Object.entries(this.adjustedValues).map(([k, v]) => ({ name: k, ...v })) : [];
            const { data } = await axios.get(`${url}?adjustedValues=${encodeURIComponent(JSON.stringify(adjustedValues))}`);

            this.categoryContainer = data.data.categoryContainer;
            this.all = data.data.all;
            this.additions = data.data.additions;
            this.assistantHours = data.data.assistantHours;
            this.otherHours = data.data.otherHours;
            this.adminHours = data.data.adminHours;
            this.overall = data.data.overall;
            this.hasAdminHours = data.data.hasAdminHours;
            this.hasOffTimeMultiplier = data.data.hasOffTimeMultiplier;
            this.show95PercentRow = data.data.show95PercentRow;
        },
        round(number) {
            if (number === 0.0) {
                return 0.0;
            }

            return (Math.round(number * 1000) / 1000).toFixed(3);
        },
        togglePlanningMode() {
            this.planningMode = !this.planningMode;
            this.fetchData();
        },
        incrementAdjustedValue(hoursCategory) {
            const key = `${hoursCategory.name} ${hoursCategory.hoursToShow}`;
            if (!this.adjustedValues.hasOwnProperty(key)) {
                this.$set(this.adjustedValues, key, { value: hoursCategory.numberOfChildren, category: hoursCategory.category, weeklyHours: hoursCategory.weeklyHours });
            }
            this.adjustedValues[key].value++;
            this.fetchData();
        },
        decrementAdjustedValue(hoursCategory) {
            const key = `${hoursCategory.name} ${hoursCategory.hoursToShow}`;
            if (!this.adjustedValues.hasOwnProperty(key)) {
                this.$set(this.adjustedValues, key, { value: hoursCategory.numberOfChildren, category: hoursCategory.category, weeklyHours: hoursCategory.weeklyHours });
            }
            if (this.adjustedValues[key].value > 0) {
                this.adjustedValues[key].value--;
            }
            this.fetchData();
        },
    },
};
</script>
